import { LoaderIcon } from '@/app/features/icons/Loader'
import { getGroupedViolationsColumnsDetailsNew } from '@/modules/drivers/constants/tables'

import { useSortableData } from '@/modules/drivers/hooks/useSortTable'
import { TableHeader } from '@/modules/drivers/shared/tableItems/TableHeader'
import React from 'react'
import { Checkbox } from '@/ui/form/primitives/checkbox'
import { ViolationDetailsRowItem } from './ViolationDetailsRowItem'
import TableEmpty from '@/modules/drivers/shared/tableItems/TableEmpty'
import { useDate } from '@/modules/drivers/stores/dateStore'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { useSideSelectionStore } from '@/modules/drivers/stores/sideSelectionStore'
import {
  useExcludedViolations,
  useViolationPrintStore,
} from '@/modules/drivers/stores/violationPrintStore'
import { TableHeaderColumn } from '@/modules/drivers/types/table'
import { useParams } from 'react-router-dom'

const ViolationDetailsTable = ({
  violations,
  isLoadingDetails,
  driverId,
}: {
  violations: any[]
  isLoadingDetails: boolean
  driverId?: number
}) => {
  const { t } = useLocalize()
  const { companyId } = useParams<{ companyId: string }>()
  const columns = getGroupedViolationsColumnsDetailsNew(t)
  const excluded = useExcludedViolations(companyId)
  const setPrinted = useViolationPrintStore((s) => s.setPrinted)
  const togglePrinted = (ids: number[], printed: boolean) => {
    if (companyId) setPrinted(companyId, ids, printed)
  }

  const { range } = useDate()
  const isLoading = isLoadingDetails

  const { sortedData, sortConfig, handleHeaderClick } = useSortableData({
    data: violations,
    tableColumns: columns,
  })

  const filteredByRange = React.useMemo(() => {
    if (!range?.startDate || !range?.endDate) return sortedData

    const rangeStart = new Date(
      range.startDate.year,
      range.startDate.month - 1,
      range.startDate.day,
    )
    const rangeEnd = new Date(
      range.endDate.year,
      range.endDate.month - 1,
      range.endDate.day,
      23,
      59,
      59,
    )

    return sortedData.filter((v) => {
      const vStart = v.startTime ? new Date(v.startTime) : null
      const vEnd = v.endTime ? new Date(v.endTime) : null
      if (!vStart) return false
      const end = vEnd || vStart
      return end >= rangeStart && vStart <= rangeEnd
    })
  }, [sortedData, range])

  const {sideCheckedIds} = useSideSelectionStore()

  const visibleIds = filteredByRange.map((v) => v.id)
  const printedCount = visibleIds.filter((id) => !excluded.has(id)).length
  const allPrinted = visibleIds.length > 0 && printedCount === visibleIds.length

  const printColumn: TableHeaderColumn = {
    title: t('common.print'),
    key: null,
    type: 'string',
    sort: false,
    headerContent: (
      <Checkbox
        title={t('common.print')}
        checked={printedCount > 0}
        indeterminate={printedCount > 0 && !allPrinted}
        onChange={() => togglePrinted(visibleIds, !allPrinted)}
        onClick={(e) => e.stopPropagation()}
        disabled={visibleIds.length === 0}
      />
    ),
  }
  const columnsWithPrint = [printColumn, ...columns]

  return (
    <section className="tw-relative tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-hidden">
      {isLoading ? (
        <div className="tw-grid tw-h-full tw-place-items-center">
          <LoaderIcon className="tw-w-16 tw-animate-spin" />
        </div>
      ) : filteredByRange.length > 0 ? (
        <div className="tw-flex tw-flex-1 tw-flex-col tw-overflow-auto">
          <table className="tw-relative tw-w-full tw-table-auto tw-border-spacing-x-2">
            <TableHeader
              opacity
              sticky
              compact
              disabledCheckboxToggle
              rounded={false}
              columns={columnsWithPrint}
              sortConfig={sortConfig}
              onHeaderClick={handleHeaderClick}
            />

            <tbody>
              {filteredByRange.map((violation, i) => (
                <ViolationDetailsRowItem
                  key={violation?.id}
                  selectedIds={sideCheckedIds}
                  violation={violation}
                  driverId={driverId}
                  printed={!excluded.has(violation?.id)}
                  onPrintedChange={(printed) =>
                    togglePrinted([violation?.id], printed)
                  }
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <>
          <table className="tw-relative tw-w-full tw-table-auto tw-border-spacing-x-2">
            <TableHeader
            disabledCheckboxToggle
              opacity
              compact
              transparent
              columns={columnsWithPrint}
              sortConfig={null}
            />
          </table>
          <TableEmpty title={t('driverModule.no-violations-in-period')} />
        </>
      )}
    </section>
  )
}

export default ViolationDetailsTable
